import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import {
	Container,
	Stack,
	AppBar,
	Toolbar,
	Typography,
	Dialog,
	IconButton,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Divider
} from '@mui/material';
import { isEmpty } from 'lodash';
import ReactToPrint from 'react-to-print';
import { useParams } from 'react-router-dom';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx/xlsx.mjs';

import { DataContext } from '../../../context/DataContext';
import { getRol, getToken, getUser } from '../../../api/Account';
import NoData from '../../../components/NoData';

import { PrintReport } from '../../../components/PrintReport';
import {
	getGestoresAsync,
	getGestoresBySucursalAccessAsync,
	getSucursalByUserAsync
} from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';
import { getNotasVisitaAsync } from '../../../api/Report';

const ReporteNotasVisita = () => {
	const [data, setData] = useState([]);

	const [gestorList, setGestorList] = useState([]);
	const [gestorName, setGestorName] = useState('');

	const compRef = useRef();

	const { selectedUser, fechaDesde, fechaHasta, isAbonado } = useParams();

	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const formatMoney = (v, isDolar) =>
		new Intl.NumberFormat(isDolar ? 'en-US' : 'es-NI', {
			style: 'currency',
			currency: isDolar ? 'USD' : 'NIO'
		}).format(v);

	const requestPayload = useMemo(
		() => ({
			gestorId: selectedUser === 't' ? 0 : Number(selectedUser),
			desde: moment(fechaDesde).format('YYYY-MM-DD'),
			hasta: moment(fechaHasta).format('YYYY-MM-DD'),
			isAbonado: isAbonado === 't' ? 0 : Number(isAbonado)
		}),
		[selectedUser, fechaDesde, fechaHasta, isAbonado]
	);

	const fetchData = useCallback(async () => {
		setIsLoading(true);

		const result = await getNotasVisitaAsync(token, requestPayload);

		setIsLoading(false);

		if (!result.statusResponse) {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setData(result.data.result);
	}, [requestPayload, token, setIsLoading, setIsUnautorized]);

	const getGestores = async () => {
		setIsLoading(true);
		const result = await getGestoresBySucursalAccessAsync(token);

		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setGestorList(result.data.result);
		let gestor = result.data.result.find((g) => g.id === Number(selectedUser));

		setGestorName(gestor.fullName);
		setIsLoading(false);
	};

	const hasFetchedRef = useRef(false);

	useEffect(() => {
		if (hasFetchedRef.current) return;
		hasFetchedRef.current = true;

		fetchData();
		getGestores();
	}, [fetchData]);

	const pageStyle = `
    @media print {
      html, body {
        height: initial !important;
        overflow: initial !important;
        -webkit-print-color-adjust: exact;
      }
    }
    @media all {
      .pagebreak {
        display: none;
      }
    }
    @media print {
      .pagebreak {
        page-break-before: always;
      }
    }  
    @page { size: landscape;  margin: 3mm; }
  `;

	const exportExcel = () => {
		const exp = data.map((item) => {
			const {
				id,
				createAt,
				fullName,
				address,
				nota,
				isAbonado,
				totalRecibido,
				isDolar,
				lat,
				lng,
				gestor,
				sucursal
			} = item;

			return {
				Id: id,
				Fecha: moment(createAt).format('DD/MM/YYYY hh:mm A'),
				Cliente: fullName,
				Direccion: address,
				Nota: nota,
				Abonado: isAbonado ? 'Sí' : 'No',
				TotalRecibido: totalRecibido,
				Moneda: isDolar ? 'USD' : 'NIO',
				Latitud: lat,
				Longitud: lng,
				Gestor: gestor,
				Sucursal: sucursal
			};
		});

		const bk = XLSX.utils.book_new();
		const bs = XLSX.utils.json_to_sheet(exp);

		XLSX.utils.book_append_sheet(bk, bs, 'NotasVisitas');

		XLSX.writeFile(bk, 'ReporteNotasVisitas.xlsx');
	};

	const paramText = (
		<Container maxWidth={false} sx={{ textAlign: 'center' }}>
			<Stack direction={'row'} justifyContent="space-between">
				<Stack>
					<Typography
						sx={{
							color: '#2196f3',
							textAlign: 'left'
						}}
						fontSize={11}
					>
						Parametros:
					</Typography>
					{/* <Typography fontSize={11} textAlign="left">
						{`Sucursal: ${selectedSucursal === 't' ? 'Todas' : sucursalName}`}
					</Typography> */}
					<Typography fontSize={11} textAlign="left">
						{`Gestor Seleccionado: ${selectedUser === 't' ? 'Todos' : gestorName}`}
					</Typography>

					<Typography fontSize={11} textAlign="left">
						{`Abonado: ${isAbonado === 't' ? 'Todos' : isAbonado}`}
					</Typography>
				</Stack>

				<Stack>
					<Typography
						sx={{
							color: '#2196f3',
							textAlign: 'left'
						}}
						fontSize={11}
					>
						Usuario:
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{user}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{rol}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{`Fecha y Hora: ${moment().format('DD/MM/YYYY hh:mm A')}`}
					</Typography>
				</Stack>
			</Stack>
		</Container>
	);

	const DataTable = ({ dataGestor = [], headerFontSize, dataFontSize, isForPrint = false }) => {
		const columns = [
			{
				key: 'id',
				label: '#',
				align: 'center',
				getValue: (r) => r.id
			},
			{
				key: 'fecha',
				label: 'Fecha',
				align: 'center',
				getValue: (r) => moment(r.createAt).format('DD/MM/YYYY hh:mm A')
			},
			{
				key: 'client',
				label: 'Cliente',
				align: 'left',
				getValue: (r) => r.fullName
			},
			{
				key: 'address',
				label: 'Dirección',
				align: 'left',
				getValue: (r) => r.address
			},
			{
				key: 'nota',
				label: 'Nota',
				align: 'left',
				getValue: (r) => r.nota
			},
			{
				key: 'isAbonado',
				label: 'Abonado',
				align: 'center',
				getValue: (r) => (r.isAbonado ? 'Sí' : 'No')
			},

			{
				key: 'totalRecibido',
				label: 'Total Recibido',
				align: 'center',
				getValue: (r) => formatMoney(r.totalRecibido, r.isDolar)
			},
			{
				key: 'sucursal',
				label: 'Sucursal',
				align: 'center',
				getValue: (r) => r.sucursal
			},
			{
				key: 'ubicacion',
				label: 'Ubicación',
				align: 'center',
				getValue: (r) => {
					if (!r.lat || !r.lng) return '-';

					return (
						<a
							href={`https://www.google.com/maps?q=${r.lat},${r.lng}`}
							target="_blank"
							rel="noopener noreferrer"
							style={{
								color: '#2979ff',
								fontSize: '18px'
							}}
						>
							<FontAwesomeIcon icon={faMapMarkerAlt} />
						</a>
					);
				}
			}
		].filter((c) => !(isForPrint && c.key === 'ubicacion'));

		const consolidatedTotals = useMemo(() => {
			return dataGestor.reduce(
				(acc, item) => {
					acc.totalNotasConsol += 1;

					if (item.isAbonado) {
						acc.totalAbonadasConsol += 1;
					} else {
						acc.totalNoAbonadasConsol += 1;
					}

					acc.totalRecibidoConsol += Number(item.totalRecibido || 0);

					return acc;
				},
				{
					totalNotasConsol: 0,
					totalAbonadasConsol: 0,

					totalNoAbonadasConsol: 0,

					totalRecibidoConsol: 0
				}
			);
		}, [dataGestor]);

		const {
			totalNotasConsol,
			totalAbonadasConsol,

			totalNoAbonadasConsol,

			totalRecibidoConsol
		} = consolidatedTotals;

		return (
			<>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								{columns.map((col) => (
									<TableCell
										key={col.key}
										align={col.align}
										style={{ fontSize: headerFontSize }}
										className="text-primary fw-bold"
									>
										{col.label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{dataGestor.map((r) => {
								return (
									<TableRow key={r.id}>
										{columns.map((col) => {
											if (col.key === 'estadoCuotas') {
												return (
													<TableCell
														key={col.key}
														align={col.align}
														style={{ fontSize: dataFontSize }}
														className={isForPrint ? 'text-dark' : ''}
													>
														<Stack>
															<span
																style={{
																	color: '#00a152'
																}}
															>{`Pagadas: ${
																col.getValue(r).cuotasPagadas
															}`}</span>
															<span
																style={{
																	color: '#f50057'
																}}
															>{`Pendientes: ${
																col.getValue(r).cuotasPendiente
															}`}</span>
														</Stack>
													</TableCell>
												);
											} else {
												return (
													<TableCell
														key={col.key}
														align={col.align}
														style={{ fontSize: dataFontSize }}
														className={isForPrint ? 'text-dark' : ''}
													>
														{col.getValue(r)}
													</TableCell>
												);
											}
										})}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>

				{/* FOOTER RESUMEN */}
				<Stack mt={2}>
					<Typography
						sx={{
							color: '#2196f3',
							textAlign: 'center',
							fontWeight: 'bold'
						}}
					>
						Consolidado
					</Typography>

					<hr />

					<Stack direction="row" justifyContent="space-around" flexWrap="wrap" gap={2}>
						<Stack textAlign="center">
							<Typography
								style={{
									fontWeight: 'bold',
									color: '#03a9f4',
									fontSize: 11
								}}
							>
								Total Visitas
							</Typography>

							<Typography style={{ fontSize: 11 }}>{totalNotasConsol}</Typography>
						</Stack>

						<Stack textAlign="center">
							<Typography
								style={{
									fontWeight: 'bold',
									color: '#03a9f4',
									fontSize: 11
								}}
							>
								Total Abonados
							</Typography>

							<Typography style={{ fontSize: 11 }}>{totalAbonadasConsol}</Typography>
						</Stack>

						<Stack textAlign="center">
							<Typography
								style={{
									fontWeight: 'bold',
									color: '#03a9f4',
									fontSize: 11
								}}
							>
								Total No Abonados
							</Typography>

							<Typography style={{ fontSize: 11 }}>
								{totalNoAbonadasConsol}
							</Typography>
						</Stack>

						<Stack textAlign="center">
							<Typography
								style={{
									fontWeight: 'bold',
									color: '#03a9f4',
									fontSize: 11
								}}
							>
								Total Recibido
							</Typography>

							<Typography style={{ fontSize: 11 }}>
								{new Intl.NumberFormat('es-NI', {
									style: 'currency',
									currency: 'NIO'
								}).format(totalRecibidoConsol)}
							</Typography>
						</Stack>
					</Stack>

					<hr />
				</Stack>
			</>
		);
	};

	return (
		<div>
			<Dialog fullScreen open={true}>
				<AppBar sx={{ position: 'relative' }}>
					<Toolbar>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
						<Typography
							sx={{ ml: 2, flex: 1, textAlign: 'center' }}
							variant="h5"
							component="div"
						>
							{`${title} `}
						</Typography>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
					</Toolbar>
				</AppBar>

				<Stack display="flex" justifyContent="center">
					<Stack
						direction={'row'}
						justifyContent="space-between"
						paddingLeft={5}
						paddingRight={5}
						marginTop={1}
						marginBottom={1}
					>
						<IconButton variant="outlined" onClick={() => exportExcel()}>
							<FontAwesomeIcon
								icon={faCloudArrowDown}
								style={{ fontSize: 40, color: '#00a152', width: 40 }}
							/>
						</IconButton>

						<Stack>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
							>
								{slogan}
							</Typography>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
								variant="h6"
								component="div"
							>
								Reporte de Rutas
							</Typography>
						</Stack>

						<ReactToPrint
							trigger={() => {
								return (
									<IconButton
										variant="outlined"
										// style={{ position: "fixed", right: 20, top: 75 }}
									>
										<PrintRoundedIcon
											style={{ fontSize: 40, color: '#2979ff', width: 40 }}
										/>
									</IconButton>
								);
							}}
							content={() => compRef.current}
							pageStyle={pageStyle}
						/>
					</Stack>

					{paramText}
				</Stack>

				<Container maxWidth={false} sx={{ textAlign: 'center' }}>
					<hr />
					{isEmpty(data) ? (
						<NoData />
					) : (
						<>
							<Divider />

							<DataTable dataGestor={data} headerFontSize={11} dataFontSize={10} />
						</>
					)}
				</Container>
			</Dialog>

			<div
				style={{
					display: 'none'
				}}
			>
				<PrintReport
					ref={compRef}
					titulo={'Reporte de Rutas'}
					fecha={`Fecha: ${moment().format('L')}`}
				>
					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						{paramText}
					</Container>

					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						<hr />

						{isEmpty(data) ? (
							<NoData />
						) : (
							<>
								<Divider />

								<DataTable
									dataGestor={data}
									headerFontSize={11}
									dataFontSize={10}
									isForPrint={true}
								/>
							</>
						)}
					</Container>
				</PrintReport>
			</div>
		</div>
	);
};

export default ReporteNotasVisita;
